import React, { useState } from "react";
import {
  Archive as ArchiveIcon,
  CheckCircle2,
  ExternalLink,
  GripVertical,
  Pencil,
} from "lucide-react";
import { useDraggable } from "@dnd-kit/core";
import { PIPELINE_STAGES, getStage } from "../../../config/pipelineStages";
import EditCustomerDetailsModal from "./EditCustomerDetailsModal";

export default function PipelineCard({
  lead,
  onArchive,
  onOpenDetail,
  onUpdateContactDetails,
  onMarkPaid,
}) {
  const [editing, setEditing] = useState(false);

  const status = lead.status || "New";
  const stage = getStage(status) || PIPELINE_STAGES[0];
  // Customer name resolution. Loading details async means lead.customer
  // may be null on first render — fall back to trade/suburb so the card
  // shows something useful instead of "Loading..." getting stuck if
  // loadLeadDetails silently fails.
  const customer =
    lead.customer ||
    lead.form?.fullName ||
    [lead.trade, lead.suburb].filter(Boolean).join(" · ") ||
    (lead.detailsLoaded ? "Unnamed customer" : "New lead");

  // Booked stage shows the scheduled date so the tradie can eyeball the
  // calendar without opening the card.
  const bookedDate =
    status === "Booked" && (lead.jobBookedDate || lead.job_booked_date)
      ? new Date(lead.jobBookedDate || lead.job_booked_date)
      : null;
  const bookedDateLabel = bookedDate
    ? bookedDate.toLocaleDateString("en-AU", {
        weekday: "short",
        day: "numeric",
        month: "short",
      })
    : null;

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: lead.id,
    data: { stage: status },
  });

  return (
    <>
      <div
        ref={setNodeRef}
        className="rqp-pipeline-card rqp-pipeline-card--compact"
        style={{
          borderLeft: `3px solid ${stage.color}`,
          opacity: isDragging ? 0.35 : 1,
        }}
      >
        <span
          {...attributes}
          {...listeners}
          aria-label="Drag to move stage"
          className="rqp-pipeline-card__drag"
        >
          <GripVertical size={14} />
        </span>

        <button
          type="button"
          className="rqp-pipeline-card__main"
          onClick={() => onOpenDetail?.(lead.id)}
        >
          <strong>{customer}</strong>
          <span>
            {bookedDateLabel
              ? `📅 ${bookedDateLabel}`
              : timeAgo(lead.createdAt || lead.created_at)}
            {lead.trade ? ` · ${lead.trade}` : ""}
          </span>
        </button>

        <div className="rqp-pipeline-card__actions">
          {status === "Invoiced" && onMarkPaid && (
            <button
              type="button"
              className="rq-icon-button"
              onClick={() => onMarkPaid(lead.id)}
              aria-label="Mark invoice as paid"
              title="Mark as paid"
              style={{ color: "#0F766E" }}
            >
              <CheckCircle2 size={14} />
            </button>
          )}
          {onUpdateContactDetails && (
            <button
              type="button"
              className="rq-icon-button"
              onClick={() => setEditing(true)}
              aria-label="Edit contact"
              title="Edit contact"
            >
              <Pencil size={14} />
            </button>
          )}
          {onOpenDetail && (
            <button
              type="button"
              className="rq-icon-button"
              onClick={() => onOpenDetail(lead.id)}
              aria-label="Open job details"
              title="Open job details"
            >
              <ExternalLink size={14} />
            </button>
          )}
          {onArchive && !lead.archived_at && (
            <button
              type="button"
              className="rq-icon-button"
              onClick={() => onArchive(lead.id)}
              aria-label="Archive job"
              title="Archive job"
            >
              <ArchiveIcon size={14} />
            </button>
          )}
        </div>
      </div>

      {editing && (
        <EditCustomerDetailsModal
          lead={lead}
          onSave={onUpdateContactDetails}
          onClose={() => setEditing(false)}
        />
      )}
    </>
  );
}

function timeAgo(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60000) return "just now";
  const m = Math.floor(ms / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  return `${mo}mo ago`;
}
